import React, { useState } from 'react';
import SparkleDust from './SparkleDust';
import { forgotPassword } from '../services/storageService';

interface AuthFormProps {
  onLogin: (email: string, name: string, password?: string, isLogin?: boolean) => Promise<void>;
  onGoogleLogin: () => Promise<void>;
}

const AuthForm: React.FC<AuthFormProps> = ({ onLogin, onGoogleLogin }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Ju lutem shkruani email-in tuaj për të resetuar fjalëkalimin.");
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await forgotPassword(email);
      setResetSent(true);
      setTimeout(() => setResetSent(false), 10000);
    } catch (err: any) {
      setError(err.message || "Ndodhi një gabim gjatë dërgimit të email-it.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password || (!isLogin && !name)) {
        setError("Ju lutem plotësoni të gjitha fushat.");
        return;
    }
    
    setLoading(true);
    try {
        await onLogin(email, name || email.split('@')[0], password, isLogin);
    } catch (err: any) {
        setError(err.message || "Ndodhi një gabim gjatë hyrjes.");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="glass-card rounded-[2.5rem] p-8 md:p-12 max-w-md mx-auto border border-white/10 animate-in zoom-in duration-500 shadow-2xl relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl"></div>
      
      <div className="text-center mb-10 relative z-10">
        <div className="flex justify-center mb-6">
          <span className="text-7xl animate-bounce">🏰</span>
        </div>
        <h2 className="text-3xl font-bold text-white font-fairytale mb-2">
          {isLogin ? 'Mirësevini përsëri' : 'Krijo Llogarinë'}
        </h2>
        <p className="text-indigo-100/80 text-sm italic">
          {isLogin ? 'Vazhdo udhëtimin tënd magjik' : 'Filloni një aventurë të re sot'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
        {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-100 text-xs text-center font-bold animate-shake">
                {error}
                {isLogin && error.toLowerCase().includes('password') && (
                  <button 
                    type="button"
                    onClick={handleForgotPassword}
                    className="block mx-auto mt-2 text-indigo-200 underline hover:text-white transition-colors"
                  >
                    Keni harruar fjalëkalimin?
                  </button>
                )}
            </div>
        )}

        {resetSent && (
          <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30 text-green-100 text-xs text-center font-bold">
            Email-i për resetimin e fjalëkalimit u dërgua! Kontrolloni inbox-in tuaj.
          </div>
        )}

        {!isLogin && (
          <div className="space-y-1.5">
            <label className="block text-[10px] font-black text-indigo-200 uppercase tracking-[0.2em] ml-2">Emri i Plotë</label>
            <input
              type="text" required placeholder="Si quheni?"
              className="w-full px-5 py-4 rounded-2xl bg-white/10 border border-white/20 outline-none text-white text-sm focus:border-indigo-500/50 focus:bg-white/20 transition-all shadow-inner"
              value={name} onChange={(e) => setName(e.target.value)}
            />
          </div>
        )}

        <div className="space-y-1.5">
          <label className="block text-[10px] font-black text-indigo-200 uppercase tracking-[0.2em] ml-2">Email Adresa</label>
          <input
            type="email" required placeholder="email@shembull.com"
            className="w-full px-5 py-4 rounded-2xl bg-white/10 border border-white/20 outline-none text-white text-sm focus:border-indigo-500/50 focus:bg-white/20 transition-all shadow-inner"
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between items-center ml-2">
            <label className="block text-[10px] font-black text-indigo-200 uppercase tracking-[0.2em]">Fjalëkalimi</label>
            {isLogin && (
              <button 
                type="button" 
                onClick={handleForgotPassword}
                className="text-[9px] font-black text-indigo-300/60 uppercase tracking-widest hover:text-white transition-colors"
              >
                Harruat?
              </button>
            )}
          </div>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"} 
              required 
              placeholder="••••••••"
              className="w-full px-5 py-4 rounded-2xl bg-white/10 border border-white/20 outline-none text-white text-sm focus:border-indigo-500/50 focus:bg-white/20 transition-all shadow-inner"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
            <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
            >
                {showPassword ? <span className="text-xl">🙈</span> : <span className="text-xl">👁️</span>}
            </button>
          </div>
        </div>

        <button 
            type="submit" 
            disabled={loading}
            className="w-full sparkle-nav-btn active py-5 rounded-2xl text-lg uppercase tracking-widest mt-4 shadow-xl active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
        >
          <SparkleDust />
          {loading ? 'Duke u lidhur...' : (
            isLogin ? (
              <>
                Hyr në Kështjellë <span className="text-2xl">🔑</span>
              </>
            ) : (
              <>
                Regjistrohu <span className="text-2xl">✨</span>
              </>
            )
          )}
        </button>

        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-[10px] uppercase tracking-[0.2em]">
            <span className="bg-[#0f172a] px-4 text-indigo-200/50">Ose vazhdo me</span>
          </div>
        </div>

        <button 
            type="button"
            onClick={onGoogleLogin}
            className="w-full py-4 rounded-2xl bg-white text-slate-900 font-bold text-sm flex items-center justify-center gap-3 hover:bg-slate-100 transition-all active:scale-95 shadow-lg"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" referrerPolicy="no-referrer" />
          Vazhdo me Google
        </button>

        <div className="pt-6 text-center">
            <p className="text-indigo-100/60 text-xs font-medium">
              {isLogin ? 'Nuk keni ende një llogari?' : 'Keni një llogari ekzistuese?'} 
              <button 
                type="button" 
                onClick={() => {
                    setIsLogin(!isLogin);
                    setError(null);
                }}
                className="ml-2 text-indigo-200 font-black hover:text-white transition-colors underline decoration-indigo-500/50 underline-offset-4"
              >
                {isLogin ? 'Krijo një tani' : 'Identifikohu këtu'}
              </button>
            </p>
        </div>
      </form>
    </div>
  );
};

export default AuthForm;